import { Box, Flex, FormControl, Input, Stack, Tag } from "@chakra-ui/react"
import { ErrorMessage, Formik } from "formik"
import { ComponentProps, FC, useState } from "react"
import { ReactSelect, SelectOption } from "src/components/shared/ReactSelect"
import { ErrorMessageField } from "src/components/ui"
import { DrawerForm } from "src/components/ui/DrawerForm"
import { InputLabel } from "src/components/ui/InputLabel"
import { IContact, ILocation } from "src/domain/entities"
import { fetchGujaratiSuggestions } from "src/utils/helpers"
import { FormikOnSubmit } from "src/utils/types"
import { IHallBookingAddFormFields } from "."
import moment from "moment"

interface Props extends Omit<ComponentProps<typeof DrawerForm>, "children"> {
	handleSubmit: FormikOnSubmit<IHallBookingAddFormFields>
	locationList: ILocation[]
	contactList: IContact[]
}

export const HallBookingAddDrawerFormView: FC<Props> = ({
	handleSubmit,
	locationList,
	contactList,
	...rest
}) => {
	const [nameSuggestions, setNameSuggestions] = useState<string[]>([])

	return (
		<Formik<IHallBookingAddFormFields>
			initialValues={{
				startDate: moment().format("YYYY-MM-DD"),
				endDate: moment().format("YYYY-MM-DD"),
				locationId: "",
				contactId: "",
				reason: "",
				deposit: 0,
				paymentRemarks: "",
			}}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{({ values, isSubmitting, handleChange, setFieldValue }) => {
				const handleNameChange = async (
					e: React.ChangeEvent<HTMLInputElement>,
				) => {
					setFieldValue("reason", e.target.value)
					const suggestions = await fetchGujaratiSuggestions(e.target.value)
					setNameSuggestions(suggestions)
				}
				return (
					<DrawerForm
						size="sm"
						headerLabel="Add Hall Booking"
						submitLabel="Save"
						isSubmitting={isSubmitting}
						{...rest}
					>
						<Stack maxWidth={"sm"} marginX={"auto"}>
							{/* Location */}
							<FormControl>
								<InputLabel label="Location" />
								<ReactSelect
									name="locationId"
									onChange={(newValue) => {
										setFieldValue(
											"locationId",
											(newValue as SelectOption).value,
										)
									}}
									options={locationList.map((location) => ({
										label: location.name,
										value: location.id,
									}))}
									isSearchable
								/>
							</FormControl>
							{/* Contact */}
							<FormControl>
								<InputLabel label="Contact" />
								<ReactSelect
									name="contactId"
									onChange={(newValue) => {
										setFieldValue(
											"contactId",
											(newValue as SelectOption).value,
										)
									}}
									options={contactList.map((contact) => ({
										label: `${contact.firstName} ${contact.lastName}`,
										value: contact.id,
									}))}
									isSearchable
								/>
							</FormControl>
							{/* Reason */}
							<FormControl>
								<InputLabel label="Reason" />
							<Input
								name="reason"
								placeholder="Reason"
								maxLength={100}
								value={values.reason}
								onChange={handleNameChange}
							/>
								<ErrorMessage
									component={ErrorMessageField}
									name="reason"
								/>
							</FormControl>
							<Box>
								{nameSuggestions.map((el, i) => (
									<Tag
										colorScheme={"green"}
										backgroundColor={"green.50"}
										variant="outline"
										_hover={{
											backgroundColor: "green.100",
										}}
										cursor="pointer"
										margin={0.5}
										onClick={() => {
											setFieldValue("reason", el)
											setNameSuggestions([])
										}}
										key={i}
									>
										{el}
									</Tag>
								))}
							</Box>
							{/* Start & End Date */}
							<Flex className="gap-2">
								<FormControl>
									<InputLabel label="From" />
									<Input
										name="startDate"
										type="date"
										value={values.startDate}
										required
										autoFocus
										onChange={handleChange}
									/>
								</FormControl>
								<FormControl>
									<InputLabel label="To" />
									<Input
										name="endDate"
										type="date"
										value={values.endDate}
										required
										autoFocus
										onChange={handleChange}
									/>
								</FormControl>
							</Flex>
							<FormControl>
								<InputLabel label="Deposit" />
								<Input
									name="deposit"
									type="number"
									value={values.deposit}
									required
									autoFocus
									onChange={handleChange}
								/>
								<ErrorMessage
									component={ErrorMessageField}
									name="deposit"
								/>
							</FormControl>
							<FormControl>
								<InputLabel label="Payment Remarks" />
							<Input
								name="paymentRemarks"
								placeholder="Payment Remarks"
								maxLength={100}
								value={values.paymentRemarks}
								onChange={handleChange}
							/>
								<ErrorMessage
									component={ErrorMessageField}
									name="paymentRemarks"
								/>
							</FormControl>
						</Stack>
					</DrawerForm>
				)
			}}
		</Formik>
	)
}
